(() => {
  'use strict';

  if (window.FurForeverFlagshipItem && typeof window.FurForeverFlagshipItem.scan === 'function') {
    window.FurForeverFlagshipItem.scan(document);
    return;
  }

  const productControllers = new WeakMap();
  const supportControllers = new WeakMap();
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESKTOP_GALLERY = window.matchMedia('(min-width: 1100px)');

  function parseJSON(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function normalise(value) {
    return String(value || '').trim().toLowerCase();
  }

  function emit(root, eventName, detail = {}) {
    const productRoot = root.matches('[data-ff-flagship-item]')
      ? root
      : document.querySelector('[data-ff-flagship-item]');
    const controller = productRoot ? productControllers.get(productRoot) : null;
    const common = {
      product_id: root.dataset.productId || (productRoot && productRoot.dataset.productId) || null,
      product_handle: root.dataset.productHandle || (productRoot && productRoot.dataset.productHandle) || null,
      section_id: root.dataset.sectionId || null,
      variant_id: controller && controller.currentVariant ? controller.currentVariant.id : null,
      currency: controller && controller.config ? controller.config.currency : null,
      price: controller && controller.currentPrice != null ? controller.currentPrice : null
    };
    const payload = Object.assign(common, detail);
    const domName = `ff:flagship:${eventName}`;
    const dataLayerName = `ff_flagship_${eventName.split('-').join('_')}`;

    root.dispatchEvent(new CustomEvent(domName, {
      bubbles: true,
      detail: payload
    }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: dataLayerName }, payload));
    }
  }

  class FlagshipProductController {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.product = parseJSON(root, '[data-ff-product-json]');
      this.config = parseJSON(root, '[data-ff-config-json]') || {};
      this.form = root.querySelector('.ff-product-form');
      this.primaryButton = root.querySelector('[data-ff-primary-cta]');
      this.variantInput = root.querySelector('[data-ff-variant-id]');
      this.engravingField = root.querySelector('[data-ff-engraving-field]');
      this.engravingInput = root.querySelector('[data-ff-engraving-input]');
      this.sticky = root.querySelector('[data-ff-sticky]');
      this.stickyButton = root.querySelector('[data-ff-sticky-button]');
      this.mediaDialog = root.querySelector('[data-ff-media-dialog]');
      this.mediaDialogImage = root.querySelector('[data-ff-media-dialog-image]');
      this.zoomOpener = null;
      this.currentVariant = null;
      this.currentPrice = null;
      this.submitPending = false;
      this.pendingResetTimer = null;
      this.scrollFrame = null;
      this.stickyFrame = null;
      this.stickyHideTimer = null;
      this.stickyVisible = false;

      if (!this.product || !Array.isArray(this.product.variants) || !this.form || !this.primaryButton) {
        return;
      }

      this.bindEvents();
      this.initGallery();
      this.initSticky();
      this.initAjaxObserver();
      this.updateEngravingCount();
      this.syncState({ updateUrl: false, dispatch: false });
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        const input = event.target.closest('[data-ff-option-input]');
        if (!input || !this.root.contains(input)) return;

        const group = input.closest('[data-ff-option-group]');
        this.clearGroupError(group);
        this.clearSummary();
        this.syncState({ updateUrl: true, dispatch: true });

        const kind = input.dataset.optionKind;
        const eventName = kind === 'engraving'
          ? 'engraving-selected'
          : 'option-selected';
        const selectionDetail = {
          field: kind || 'option',
          value: input.value
        };
        emit(this.root, eventName, selectionDetail);
      }, { signal: this.signal });

      this.root.addEventListener('input', (event) => {
        if (event.target !== this.engravingInput) return;

        const start = this.engravingInput.selectionStart;
        const end = this.engravingInput.selectionEnd;
        this.engravingInput.value = this.engravingInput.value.toLocaleUpperCase();
        try {
          this.engravingInput.setSelectionRange(start, end);
        } catch (error) {
          // Selection APIs are not available for every input mode.
        }

        this.updateEngravingCount();
        if (this.isEngravingValid()) this.clearEngravingError();
        this.syncState({ updateUrl: false, dispatch: false });
      }, { signal: this.signal });

      this.root.addEventListener('click', (event) => {
        const zoomTrigger = event.target.closest('[data-ff-media-zoom]');
        if (zoomTrigger && this.root.contains(zoomTrigger)) {
          this.openMediaDialog(zoomTrigger);
          return;
        }

        const dialogClose = event.target.closest('[data-ff-media-dialog-close]');
        if (dialogClose && this.root.contains(dialogClose)) {
          this.closeMediaDialog();
          return;
        }

        const configuratorLink = event.target.closest('[data-ff-configurator-link]');
        if (configuratorLink && this.root.contains(configuratorLink)) {
          emit(this.root, 'configurator-link-selected', { location: 'product-introduction' });
        }

        const thumb = event.target.closest('[data-ff-media-thumb]');
        if (thumb && this.root.contains(thumb)) {
          this.selectMedia(String(thumb.dataset.mediaId), true);
        }
      }, { signal: this.signal });

      window.addEventListener('submit', (event) => {
        if (event.target !== this.form) return;

        const ajaxEnabled = this.form.dataset.ajaxAddToCart === 'true';
        const isNativeAjaxFallback = this.submitPending && !ajaxEnabled;

        if (!isNativeAjaxFallback) {
          emit(this.root, 'add-to-cart-attempted', {
            source: event.submitter === this.primaryButton ? 'primary' : 'form'
          });
        }

        if ((this.submitPending && ajaxEnabled) || !this.validate({ focus: true })) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        // Expression deliberately changes this attribute to false before its
        // native fallback submission when an AJAX request fails. Once the
        // configuration has been revalidated, that recovery submit must pass.
        if (!ajaxEnabled) return;

        this.submitPending = true;
        this.syncState({ updateUrl: false, dispatch: false });
        window.clearTimeout(this.pendingResetTimer);
        this.pendingResetTimer = window.setTimeout(() => {
          this.submitPending = false;
          this.syncState({ updateUrl: false, dispatch: false });
        }, 15000);
      }, { capture: true, signal: this.signal });

      if (this.stickyButton) {
        this.stickyButton.addEventListener('click', () => {
          if (this.submitPending) return;

          if (!this.isCompleteAndValid()) {
            emit(this.root, 'add-to-cart-attempted', { source: 'sticky-incomplete' });
            this.validate({ focus: true });
            return;
          }

          this.form.requestSubmit(this.primaryButton);
        }, { signal: this.signal });
      }
    }

    getGroups() {
      return Array.from(this.root.querySelectorAll('[data-ff-option-group]'));
    }

    getSelectedOptions() {
      const optionCount = Array.isArray(this.product.options) ? this.product.options.length : 0;
      const selected = Array(optionCount).fill(null);

      this.getGroups().forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        const input = group.querySelector('[data-ff-option-input]:checked');
        if (position > 0 && position <= selected.length && input) {
          selected[position - 1] = input.value;
        }
      });

      return selected;
    }

    variantMatches(variant, selections) {
      return selections.every((selection, index) => selection == null || variant.options[index] === selection);
    }

    findVariant(selections) {
      if (this.getGroups().length === 0 && this.product.variants.length === 1) {
        return this.product.variants[0];
      }
      if (selections.some((selection) => selection == null)) return null;
      return this.product.variants.find((variant) => this.variantMatches(variant, selections)) || null;
    }

    matchingVariants(selections) {
      return this.product.variants.filter((variant) => this.variantMatches(variant, selections));
    }

    getGroupByKind(kind) {
      return this.root.querySelector(`[data-ff-option-group][data-ff-option-kind="${kind}"]`);
    }

    selectedValueForKind(kind) {
      const group = this.getGroupByKind(kind);
      const input = group ? group.querySelector('[data-ff-option-input]:checked') : null;
      return input ? input.value : null;
    }

    displayLabelForGroup(group) {
      if (!group) return 'an option';
      const kind = group.dataset.optionKind;
      if (kind === 'other') {
        const optionName = normalise(group.dataset.optionName);
        if (optionName.startsWith('include ') || optionName.startsWith('add ')) {
          return `whether to ${optionName}`;
        }
        return optionName || 'an option';
      }
      return kind || 'an option';
    }

    summaryLabelForGroup(group) {
      const optionName = normalise(group && group.dataset.optionName).replace(/^include\s+/, '');
      return optionName || 'option';
    }

    engravingIsSelected() {
      const selected = this.selectedValueForKind('engraving');
      return normalise(selected).slice(0, 3) === 'yes';
    }

    isEngravingValid() {
      if (!this.engravingIsSelected()) return true;
      if (!this.engravingInput) return false;
      const value = this.engravingInput.value;
      return value.trim().length > 0 && value.length <= 25;
    }

    isCompleteAndValid() {
      return Boolean(this.currentVariant && this.currentVariant.available && this.isEngravingValid());
    }

    syncState({ updateUrl, dispatch }) {
      const selections = this.getSelectedOptions();
      const matching = this.matchingVariants(selections);
      const variant = this.findVariant(selections);
      this.currentVariant = variant;

      if (this.variantInput) {
        this.variantInput.value = variant ? String(variant.id) : '';
      }

      this.syncEngraving();
      this.updateAvailability(selections);
      this.updatePrice(variant, matching);
      this.updateButtonsAndStatus(selections);

      if (variant && variant.featured_media && variant.featured_media.id) {
        this.selectMedia(String(variant.featured_media.id), false);
      }

      if (updateUrl) this.updateUrl(variant);

      if (dispatch) {
        this.root.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.form,
            variant,
            selectedOptions: selections
          }
        }));
      }
    }

    syncEngraving() {
      if (!this.engravingField || !this.engravingInput) return;
      const enabled = this.engravingIsSelected();
      this.engravingField.hidden = !enabled;
      this.engravingInput.disabled = !enabled;
      this.engravingInput.required = enabled;

      if (!enabled) {
        this.clearEngravingError();
      }
    }

    updateAvailability(selections) {
      this.getGroups().forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        group.querySelectorAll('[data-ff-option-input]').forEach((input) => {
          const candidate = selections.slice();
          candidate[position] = input.value;
          const possible = this.product.variants.some((variant) => variant.available && this.variantMatches(variant, candidate));
          const card = input.closest('.ff-option-card');
          if (card) card.classList.toggle('is-unavailable', !possible);
          input.disabled = !possible;
          input.setAttribute('aria-disabled', possible ? 'false' : 'true');
        });
      });
    }

    updatePrice(variant, matching) {
      let price = Number(this.product.price_min || this.config.basePrice || 0);
      if (variant) {
        price = Number(variant.price);
      } else if (matching.length > 0) {
        price = Math.min(...matching.map((item) => Number(item.price)));
      }
      this.currentPrice = price;

      const formatted = this.formatMoney(price);
      this.root.querySelectorAll('[data-ff-price], [data-ff-form-total], [data-ff-sticky-price]').forEach((element) => {
        element.textContent = formatted;
      });

      const prefix = this.root.querySelector('[data-ff-price-prefix]');
      if (prefix) prefix.hidden = Boolean(variant);
    }

    formatMoney(cents) {
      const themeMoney = window.theme && window.theme.Shopify && window.theme.Shopify.formatMoney;
      const shopifyMoney = window.Shopify && window.Shopify.formatMoney;
      const moneyFormatter = typeof themeMoney === 'function' ? themeMoney : shopifyMoney;
      const moneyContext = typeof themeMoney === 'function' ? window.theme.Shopify : window.Shopify;

      if (typeof moneyFormatter === 'function' && this.config.moneyFormat) {
        const temporary = document.createElement('span');
        temporary.innerHTML = moneyFormatter.call(moneyContext, cents, this.config.moneyFormat);
        return temporary.textContent;
      }

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: this.config.currency || 'GBP'
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2);
      }
    }

    updateButtonsAndStatus(selections) {
      const missingGroups = this.getGroups().filter((group) => !group.querySelector('[data-ff-option-input]:checked'));
      const status = this.root.querySelector('[data-ff-form-status]');
      const stickyOptions = this.root.querySelector('[data-ff-sticky-options]');
      const engravingValid = this.isEngravingValid();
      const complete = Boolean(this.currentVariant && this.currentVariant.available && engravingValid);
      const configuredCtaLabel = complete && this.config.showPriceInCta
        ? `${this.config.ctaLabel}: ${this.formatMoney(this.currentPrice)}`
        : this.config.ctaLabel;

      this.primaryButton.disabled = !complete || this.submitPending;
      this.primaryButton.dataset.addToCartText = configuredCtaLabel;
      this.primaryButton.textContent = this.currentVariant && !this.currentVariant.available
        ? this.config.soldOutLabel
        : configuredCtaLabel;

      if (status) {
        if (missingGroups.length > 0) {
          const labels = missingGroups.map((group) => this.displayLabelForGroup(group));
          status.textContent = `Choose ${this.joinLabels(labels)} to continue.`;
        } else if (!this.currentVariant) {
          status.textContent = 'This combination is not available. Please review your choices.';
        } else if (!this.currentVariant.available) {
          status.textContent = 'This combination is currently unavailable.';
        } else if (!engravingValid) {
          status.textContent = 'Enter your engraving text to continue.';
        } else {
          status.textContent = '';
        }
      }

      const summaryValues = this.getGroups()
        .filter((group) => group.dataset.optionKind !== 'engraving')
        .map((group) => {
          const kind = group.dataset.optionKind;
          const input = group.querySelector('[data-ff-option-input]:checked');
          if (!input) return null;
          if (kind === 'other') {
            const label = this.summaryLabelForGroup(group);
            const displayLabel = label.charAt(0).toUpperCase() + label.slice(1);
            const value = input.value.replace(/\s*\(\+[^)]*\)\s*$/, '');
            return `${displayLabel}: ${value}`;
          }
          return input.value;
        })
        .filter(Boolean);
      if (stickyOptions) {
        stickyOptions.textContent = summaryValues.length > 0 ? summaryValues.join(' · ') : 'Review your keepsake';
      }

      if (this.stickyButton) {
        this.stickyButton.disabled = this.submitPending;
        if (complete) {
          this.stickyButton.textContent = this.config.completeStickyLabel;
        } else if (missingGroups.length > 0) {
          this.stickyButton.textContent = this.config.incompleteLabel;
        } else if (!engravingValid) {
          this.stickyButton.textContent = 'Add engraving text';
        } else if (this.currentVariant && !this.currentVariant.available) {
          this.stickyButton.textContent = this.config.soldOutLabel;
        } else {
          this.stickyButton.textContent = 'Review your choices';
        }
      }
    }

    joinLabels(labels) {
      const clean = labels.filter(Boolean);
      if (clean.length < 2) return clean[0] || 'your options';
      if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
      return `${clean.slice(0, -1).join(', ')}, and ${clean[clean.length - 1]}`;
    }

    updateUrl(variant) {
      const url = new URL(window.location.href);
      if (variant) {
        url.searchParams.set('variant', variant.id);
        url.searchParams.delete('option_values');
      } else {
        url.searchParams.delete('variant');
      }
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    validate({ focus }) {
      this.clearAllErrors();
      const missing = this.getGroups().find((group) => !group.querySelector('[data-ff-option-input]:checked'));

      if (missing) {
        this.showGroupError(missing);
        this.showSummary(`Please choose ${this.displayLabelForGroup(missing)} before adding your keepsake.`);
        emit(this.root, 'validation-error', {
          field: missing.dataset.optionKind,
          error_code: 'missing_option'
        });
        if (focus) this.focusGroup(missing);
        return false;
      }

      if (!this.currentVariant) {
        this.showSummary('That combination is not available. Please review your choices.');
        emit(this.root, 'validation-error', { field: 'variant', error_code: 'invalid_combination' });
        if (focus) this.focusGroup(this.getGroups()[0]);
        return false;
      }

      if (!this.currentVariant.available) {
        this.showSummary('That combination is currently unavailable. Please choose another option.');
        emit(this.root, 'validation-error', { field: 'variant', error_code: 'unavailable_variant' });
        if (focus) this.focusGroup(this.getGroups()[0]);
        return false;
      }

      if (!this.isEngravingValid()) {
        this.showEngravingError();
        this.showSummary('Please enter your engraving text before adding your keepsake.');
        emit(this.root, 'engraving-validation-error', {
          field: 'engraving',
          error_code: 'blank_engraving'
        });
        emit(this.root, 'validation-error', {
          field: 'engraving',
          error_code: 'blank_engraving'
        });
        if (focus && this.engravingInput) {
          this.scrollAndFocus(this.engravingInput);
        }
        return false;
      }

      return true;
    }

    showGroupError(group) {
      if (!group) return;
      const error = group.querySelector('[data-ff-option-error]');
      if (error) error.hidden = false;
      group.querySelectorAll('[data-ff-option-input]').forEach((input) => input.setAttribute('aria-invalid', 'true'));
    }

    clearGroupError(group) {
      if (!group) return;
      const error = group.querySelector('[data-ff-option-error]');
      if (error) error.hidden = true;
      group.querySelectorAll('[data-ff-option-input]').forEach((input) => input.removeAttribute('aria-invalid'));
    }

    focusGroup(group) {
      const input = group ? group.querySelector('[data-ff-option-input]') : null;
      if (input) this.scrollAndFocus(input);
    }

    showEngravingError() {
      if (this.engravingInput) this.engravingInput.setAttribute('aria-invalid', 'true');
      const error = this.root.querySelector('[data-ff-engraving-error]');
      if (error) error.hidden = false;
    }

    clearEngravingError() {
      if (this.engravingInput) this.engravingInput.removeAttribute('aria-invalid');
      const error = this.root.querySelector('[data-ff-engraving-error]');
      if (error) error.hidden = true;
    }

    clearAllErrors() {
      this.getGroups().forEach((group) => this.clearGroupError(group));
      this.clearEngravingError();
      this.clearSummary();
    }

    showSummary(message) {
      const summary = this.root.querySelector('[data-ff-error-summary]');
      if (!summary) return;
      summary.textContent = message;
      summary.hidden = false;
    }

    clearSummary() {
      const summary = this.root.querySelector('[data-ff-error-summary]');
      if (!summary) return;
      summary.textContent = '';
      summary.hidden = true;
    }

    scrollAndFocus(element) {
      element.scrollIntoView({
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
        block: 'center'
      });
      window.setTimeout(() => element.focus({ preventScroll: true }), REDUCED_MOTION.matches ? 0 : 180);
    }

    updateEngravingCount() {
      const count = this.root.querySelector('[data-ff-engraving-count]');
      if (count && this.engravingInput) count.textContent = `${this.engravingInput.value.length} / 25`;
    }

    initGallery() {
      const track = this.root.querySelector('[data-ff-media-track]');
      if (!track) return;

      if (this.mediaDialog) {
        this.mediaDialog.addEventListener('click', (event) => {
          const surface = event.target.closest('.ff-media-dialog__surface');
          if (event.target === this.mediaDialog || surface === event.target) {
            this.closeMediaDialog();
          }
        }, { signal: this.signal });

        this.mediaDialog.addEventListener('close', () => {
          if (this.mediaDialogImage) {
            this.mediaDialogImage.removeAttribute('src');
            this.mediaDialogImage.alt = '';
          }
          if (this.zoomOpener && this.zoomOpener.isConnected) this.zoomOpener.focus();
          this.zoomOpener = null;
        }, { signal: this.signal });
      }

      track.addEventListener('scroll', () => {
        if (DESKTOP_GALLERY.matches) return;
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => {
          const slides = Array.from(track.querySelectorAll('[data-ff-media-slide]'));
          if (slides.length === 0) return;
          const nearest = slides.reduce((best, slide) => (
            Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(best.offsetLeft - track.scrollLeft) ? slide : best
          ));
          this.markCurrentMedia(String(nearest.dataset.mediaId));
        });
      }, { passive: true, signal: this.signal });
    }

    openMediaDialog(trigger) {
      const src = trigger.dataset.fullSrc;
      if (!src) return;

      if (!this.mediaDialog || !this.mediaDialogImage || typeof this.mediaDialog.showModal !== 'function') {
        window.open(src, '_blank', 'noopener,noreferrer');
        return;
      }

      this.zoomOpener = trigger;
      this.mediaDialogImage.src = src;
      this.mediaDialogImage.alt = trigger.dataset.mediaAlt || '';
      if (!this.mediaDialog.open) this.mediaDialog.showModal();
    }

    closeMediaDialog() {
      if (this.mediaDialog && this.mediaDialog.open) this.mediaDialog.close();
    }

    selectMedia(mediaId, userInitiated) {
      const track = this.root.querySelector('[data-ff-media-track]');
      const slide = this.root.querySelector(`[data-ff-media-slide][data-media-id="${CSS.escape(mediaId)}"]`);
      if (!track || !slide) return;

      if (!DESKTOP_GALLERY.matches) {
        const left = slide.offsetLeft;
        if (Math.abs(track.scrollLeft - left) > 2) {
          track.scrollTo({
            left,
            behavior: userInitiated && !REDUCED_MOTION.matches ? 'smooth' : 'auto'
          });
        }
      }
      this.markCurrentMedia(mediaId);
    }

    markCurrentMedia(mediaId) {
      this.root.querySelectorAll('[data-ff-media-slide]').forEach((slide) => {
        slide.classList.toggle('is-current', String(slide.dataset.mediaId) === mediaId);
      });
      this.root.querySelectorAll('[data-ff-media-thumb]').forEach((thumb) => {
        const current = String(thumb.dataset.mediaId) === mediaId;
        thumb.classList.toggle('is-current', current);
        if (current) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    initSticky() {
      if (!this.sticky || !this.primaryButton) return;

      const updateFromPosition = () => {
        if (this.stickyFrame) window.cancelAnimationFrame(this.stickyFrame);
        this.stickyFrame = window.requestAnimationFrame(() => {
          this.stickyFrame = null;
          const primaryRect = this.primaryButton.getBoundingClientRect();
          const footer = document.querySelector('.footer-group');
          const footerRect = footer ? footer.getBoundingClientRect() : null;
          const footerVisible = Boolean(
            footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0
          );
          this.setStickyVisible(primaryRect.bottom < 0 && !footerVisible);
        });
      };

      window.addEventListener('scroll', updateFromPosition, { passive: true, signal: this.signal });
      window.addEventListener('resize', updateFromPosition, { passive: true, signal: this.signal });
      updateFromPosition();
    }

    setStickyVisible(visible) {
      if (!this.sticky || visible === this.stickyVisible) return;
      this.stickyVisible = visible;
      window.clearTimeout(this.stickyHideTimer);

      if (visible) {
        this.sticky.hidden = false;
        window.requestAnimationFrame(() => this.sticky.classList.add('is-visible'));
        document.documentElement.classList.add('ff-sticky-active');
      } else {
        this.sticky.classList.remove('is-visible');
        document.documentElement.classList.remove('ff-sticky-active');
        this.stickyHideTimer = window.setTimeout(() => {
          if (!this.stickyVisible) this.sticky.hidden = true;
        }, REDUCED_MOTION.matches ? 0 : 200);
      }
    }

    initAjaxObserver() {
      this.ajaxObserver = new MutationObserver((mutations) => {
        const addedNodes = mutations.flatMap((mutation) => Array.from(mutation.addedNodes));
        const addedNotice = addedNodes
          .filter((node) => node.nodeType === Node.ELEMENT_NODE)
          .map((node) => node.matches('.ajax-add-notice--added') ? node : node.querySelector('.ajax-add-notice--added'))
          .find(Boolean);
        const errorNotice = addedNodes
          .filter((node) => node.nodeType === Node.ELEMENT_NODE)
          .map((node) => node.matches('.ajax-add-notice--error') ? node : node.querySelector('.ajax-add-notice--error'))
          .find(Boolean);

        if (addedNotice && addedNotice.dataset.ffSuccessEmitted !== 'true') {
          addedNotice.dataset.ffSuccessEmitted = 'true';
          emit(this.root, 'add-to-cart-succeeded', { source: 'ajax' });
          this.resetPending(1200);
        } else if (errorNotice) {
          this.resetPending(0);
        }
      });
      this.ajaxObserver.observe(this.form, { childList: true, subtree: true });
    }

    resetPending(delay) {
      window.clearTimeout(this.pendingResetTimer);
      this.pendingResetTimer = window.setTimeout(() => {
        this.submitPending = false;
        this.syncState({ updateUrl: false, dispatch: false });
      }, delay);
    }

    destroy() {
      this.abortController.abort();
      if (this.ajaxObserver) this.ajaxObserver.disconnect();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.stickyFrame) window.cancelAnimationFrame(this.stickyFrame);
      window.clearTimeout(this.pendingResetTimer);
      window.clearTimeout(this.stickyHideTimer);
      if (this.stickyVisible) document.documentElement.classList.remove('ff-sticky-active');
    }
  }

  class FlagshipSupportController {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      root.addEventListener('click', (event) => {
        const configuratorLink = event.target.closest('[data-ff-configurator-link]');
        if (configuratorLink && root.contains(configuratorLink)) {
          emit(root, 'configurator-link-selected', {
            location: configuratorLink.dataset.ffLinkLocation || 'support-section'
          });
        }
      }, { signal: this.signal });

      root.querySelectorAll('[data-ff-process-details]').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) {
            emit(root, 'process-details-opened', {
              question: details.querySelector('summary')?.textContent.trim() || null
            });
          }
        }, { signal: this.signal });
      });
    }

    destroy() {
      this.abortController.abort();
    }
  }

  function rootsWithin(scope, selector) {
    const roots = [];
    if (scope instanceof Element && scope.matches(selector)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(selector));
    return roots;
  }

  function scan(scope = document) {
    rootsWithin(scope, '[data-ff-flagship-item]').forEach((root) => {
      if (!productControllers.has(root)) {
        productControllers.set(root, new FlagshipProductController(root));
      }
    });

    rootsWithin(scope, '[data-ff-flagship-item-support]').forEach((root) => {
      if (!supportControllers.has(root)) {
        supportControllers.set(root, new FlagshipSupportController(root));
      }
    });
  }

  function destroy(scope) {
    rootsWithin(scope, '[data-ff-flagship-item]').forEach((root) => {
      const controller = productControllers.get(root);
      if (controller) controller.destroy();
      productControllers.delete(root);
    });

    rootsWithin(scope, '[data-ff-flagship-item-support]').forEach((root) => {
      const controller = supportControllers.get(root);
      if (controller) controller.destroy();
      supportControllers.delete(root);
    });
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window.FurForeverFlagshipItem = {
    version: '1.0.0',
    scan,
    destroy
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
